// Pending moves approval banner component

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import '../shared/token-thumbnail.js';

/**
 * @typedef {object} Point
 * @property {number} col - Column
 * @property {number} row - Row
 */

/**
 * @typedef {object} PendingPlayerMove
 * @property {string} tokenId - Token ID
 * @property {Point[]} path - Movement path
 * @property {string} [cancelledBy] - Player who cancelled the move
 */

/**
 * @typedef {object} Token
 * @property {string} id - Token ID
 * @property {string} name - Token name
 * @property {string} [imageUrl] - Token image URL
 * @property {boolean} [hasCircleBorder] - Has circle border
 * @property {string} [borderColor] - Border color
 */

/**
 * Pending moves approval banner component
 * Displays pending PC moves for party leader approval
 */
class PendingMovesApprovalBanner extends BaseComponent {
  static properties = {
    pendingMoves: { type: Array, attribute: 'pending-moves' },
    tokens: { type: Array },
  };

  static styles = css`
    :host {
      display: block;
    }

    .pending-moves-banner {
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      border: 1px solid #555;
      backdrop-filter: blur(4px);
      max-width: 500px;
    }

    .pending-moves-header {
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .pending-moves-title {
      font-size: 16px;
      font-weight: bold;
    }

    .pending-moves-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .pending-move-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
    }

    .pending-move-info {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .pending-move-name {
      font-size: 14px;
    }

    .pending-move-actions {
      display: flex;
      gap: 12px;
      margin-left: 16px;
    }

    .approve-button {
      background: #22c55e;
      border: 1px solid #16a34a;
      color: white;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .approve-button:hover {
      background: #16a34a;
    }

    .cancel-button {
      background: #ef4444;
      border: 1px solid #dc2626;
      color: white;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .cancel-button:hover {
      background: #dc2626;
    }
  `;

  constructor() {
    super();
    /** @type {PendingPlayerMove[]} */
    this.pendingMoves = [];
    /** @type {Token[]} */
    this.tokens = [];
  }

  /** @param {string} tokenId */
  _getToken(tokenId) {
    return this.tokens.find((t) => t.id === tokenId);
  }

  /** @param {string} tokenId */
  _handleApprove(tokenId) {
    this.dispatchEvent(
      new CustomEvent('approve', {
        detail: tokenId,
        bubbles: true,
      }),
    );
  }

  /** @param {string} tokenId */
  _handleCancel(tokenId) {
    this.dispatchEvent(
      new CustomEvent('cancel', {
        detail: tokenId,
        bubbles: true,
      }),
    );
  }

  _renderFootprintsIcon() {
    return html`
      <svg
        width="20"
        height="20"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"
        />
        <path
          d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"
        />
        <path d="M16 17h4" />
        <path d="M4 13h4" />
      </svg>
    `;
  }

  render() {
    if (this.pendingMoves.length === 0) {
      return html``;
    }

    return html`
      <div class="pending-moves-banner">
        <div class="pending-moves-header">
          ${this._renderFootprintsIcon()}
          <span class="pending-moves-title">Pending PC Moves</span>
        </div>
        <div class="pending-moves-list">
          ${this.pendingMoves.map((move) => {
            const token = this._getToken(move.tokenId);
            return html`
              <div class="pending-move-row">
                <div class="pending-move-info">
                  ${
                    token
                      ? html`
                        <token-thumbnail
                          image-url="${token.imageUrl || ''}"
                          name="${token.name}"
                          ?has-circle-border="${token.hasCircleBorder || false}"
                          border-color="${token.borderColor || '#444'}"
                          size="32"
                        ></token-thumbnail>
                      `
                      : ''
                  }
                  <span class="pending-move-name">${token?.name || 'Unknown'}</span>
                </div>
                <div class="pending-move-actions">
                  <button class="approve-button" @click="${() => this._handleApprove(move.tokenId)}">Approve</button>
                  <button class="cancel-button" @click="${() => this._handleCancel(move.tokenId)}">Cancel</button>
                </div>
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }
}

customElements.define('pending-moves-approval-banner', PendingMovesApprovalBanner);
